import { useCallback, useEffect, useMemo, useState } from 'react'
import { ProjectPlanDetail } from '../../../../../../../lib/functions/projectPlan'
import { useProjectPlanSnapshotRepository } from '../../../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { AgGridTreeHelper } from '../../../../../../containers/BulkSheetView/lib/tree'
import { ProjectPlanSelectRow } from '../model'

type DataSourceType = 'snapshot' | 'template'

interface UseProjectPlanSelectDataProps {
  type: DataSourceType
  uuid: string
  selectedUuids: string[]
}

const projectDataCache = new Map<
  string,
  Omit<ProjectPlanSelectRow, 'selected'>[]
>()

export const clearProjectDataCache = () => {
  projectDataCache.clear()
}

export const useProjectPlanSelectData = ({
  type,
  uuid,
  selectedUuids,
}: UseProjectPlanSelectDataProps) => {
  const [partialRows, setPartialRows] = useState<
    Omit<ProjectPlanSelectRow, 'selected'>[]
  >([])

  const { fetchMasterSchedule, fetchAllLeafUuids } =
    useProjectPlanSnapshotRepository()

  const fetchSnapshotData = useCallback(
    async (snapshotUuid: string) => {
      const response = await fetchMasterSchedule(snapshotUuid)
      return response.map(resp => ({
        uuid: resp.uuid,
        treeValue: resp.path,
        displayName: resp.wbsItem.displayName,
        type: resp.type,
      }))
    },
    [fetchMasterSchedule]
  )

  const fetchTemplateData = useCallback(
    async (
      projectUuid: string
    ): Promise<Omit<ProjectPlanSelectRow, 'selected'>[]> => {
      try {
        if (projectDataCache.has(projectUuid)) {
          return projectDataCache.get(projectUuid)!
        }

        const response = await fetchAllLeafUuids(projectUuid)

        if (!response.json) {
          const emptyResult: Omit<ProjectPlanSelectRow, 'selected'>[] = []
          projectDataCache.set(projectUuid, emptyResult)
          return emptyResult
        }

        let treeData: ProjectPlanDetail[]
        if (Array.isArray(response.json)) {
          treeData = response.json
        } else if (response.json.children) {
          treeData = response.json.children
        } else {
          treeData = [response.json]
        }

        const flattenedRows: Omit<ProjectPlanSelectRow, 'selected'>[] = []
        for (const tree of treeData) {
          const converted = AgGridTreeHelper.convert(
            tree,
            (node: ProjectPlanDetail) => ({
              uuid: node.uuid,
              treeValue: [],
              displayName: node.wbsItem?.displayName || '',
              type: node.type,
            })
          )
          for (const row of converted) {
            flattenedRows.push(row)
          }
        }

        projectDataCache.set(projectUuid, flattenedRows)
        return flattenedRows
      } catch (error) {
        console.error('Error in fetchTemplateData:', error)
        const emptyResult: Omit<ProjectPlanSelectRow, 'selected'>[] = []
        projectDataCache.set(projectUuid, emptyResult)
        return emptyResult
      }
    },
    [fetchAllLeafUuids]
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result: Omit<ProjectPlanSelectRow, 'selected'>[]

        if (type === 'snapshot') {
          result = await fetchSnapshotData(uuid)
        } else {
          result = await fetchTemplateData(uuid)
        }

        setPartialRows(result)
      } catch (error) {
        console.error('Failed to fetch project plan data:', error)
        setPartialRows([])
      }
    }

    fetchData()
  }, [type, uuid, fetchSnapshotData, fetchTemplateData])

  const rows = useMemo(() => {
    const selectedSet = new Set(selectedUuids)
    return partialRows.map(r => {
      const selected = selectedSet.has(r.uuid)
      return {
        ...r,
        selected,
      } as ProjectPlanSelectRow
    })
  }, [partialRows, selectedUuids])

  return { rows }
}

export const useProjectPlanSelectSnapshotData = (
  snapshotUuid: string,
  selectedUuids: string[]
) => {
  return useProjectPlanSelectData({
    type: 'snapshot',
    uuid: snapshotUuid,
    selectedUuids,
  })
}

export const useProjectPlanSelectTemplateData = (
  projectUuid: string,
  selectedUuids: string[]
) => {
  return useProjectPlanSelectData({
    type: 'template',
    uuid: projectUuid,
    selectedUuids,
  })
}
